import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import authService from "../services/discordAuth";
import usePageTitle from "../hooks/usePageTitle";
import "./BotDetailsPage.css";
import "../components/ReportModal.css";
import VoteModal from "../components/VoteModal";
import SuccessModal from "../components/SuccessModal";
import ReactMarkdown from "react-markdown";
import UpdateBotModal from "../components/UpdateBotModal";
import ConfirmModal from "../components/ConfirmModal";
import { trackEvent } from "../utils/analytics";
import BusinessBotView from "../components/BusinessBotView";

function BotDetailsPage() {
  const { t, i18n } = useTranslation();
  const { botId } = useParams();
  const navigate = useNavigate();

  // Estados
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [userComment, setUserComment] = useState("");
  const [commandsPage, setCommandsPage] = useState(1);
  const [reviewsPage, setReviewsPage] = useState(1);

  const [botUpdates, setBotUpdates] = useState([]);
  const [updatesPage, setUpdatesPage] = useState(1);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: "", message: "" });
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [showCooldownModal, setShowCooldownModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [shouldReopenReportModal, setShouldReopenReportModal] = useState(false);

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [updateToDeleteId, setUpdateToDeleteId] = useState(null);

  // Estados para DaaS (Dashboard as a Service)
  const [showServerModal, setShowServerModal] = useState(false);
  const [userGuilds, setUserGuilds] = useState([]);
  const [loadingGuilds, setLoadingGuilds] = useState(false);
  const [verifyingPresence, setVerifyingPresence] = useState(false);

  // SEO Dinâmico
  usePageTitle(
    bot ? `${bot.name}` : t("bot_details.hero.page_title_default"),
    bot ? `${bot.name} - ${bot.shortDescription || bot.tagline || ""}. Encontre os melhores bots Discord na BotGate.` : t("bot_details.hero.loading"),
  );

  // Buscar detalhes do bot da API
  const fetchBotDetails = async () => {
    try {
      setLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(`${BACKEND_URL}/api/bots/${botId}`);

      if (!response.ok) {
        throw new Error(t("bot_details.hero.error_not_found"));
      }

      const data = await response.json();
      setBot(data);
    } catch (err) {
      console.error("Erro ao buscar detalhes do bot:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Buscar atualizações do bot
  const fetchBotUpdates = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(`${BACKEND_URL}/api/bots/${botId}/updates`);
      const data = await response.json();

      if (data.success) {
        setBotUpdates(data.updates);
      }
    } catch (err) {
      console.error("Erro ao buscar atualizações do bot:", err);
    }
  };

  useEffect(() => {
    fetchBotDetails();
    fetchBotUpdates();
    setCommandsPage(1);
    setReviewsPage(1);
    setUpdatesPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  // Resetar páginas ao trocar de aba
  useEffect(() => {
    setCommandsPage(1);
    setReviewsPage(1);
    setUpdatesPage(1);
  }, [activeTab]);

  // Função para publicar avaliação
  const handlePublishReview = async () => {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        setModalConfig({
          title: t("bot_details.modals.auth.title"),
          message: t("bot_details.modals.auth.review_message"),
        });
        setShowSuccessModal(true);
        return;
      }

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(`${BACKEND_URL}/api/bots/${botId}/reviews`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          rating: userRating,
          comment: userComment,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setModalConfig({
          title: t("bot_details.reviews.success.title"),
          message: t("bot_details.reviews.success.message"),
        });
        setShowSuccessModal(true);

        // Limpar campos e recarregar dados
        setUserRating(0);
        setUserComment("");
        fetchBotDetails();
      } else {
        setModalConfig({
          title: t("bot_details.modals.error.ops"),
          message: data.error || t("bot_details.reviews.error.message"),
        });
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error("Erro ao publicar avaliação:", err);
      setModalConfig({
        title: t("bot_details.modals.error.title"),
        message: t("bot_details.reviews.error.server"),
      });
      setShowSuccessModal(true);
    }
  };

  // Função para verificar cooldown e abrir modal de voto
  const handleVoteClick = async () => {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        setModalConfig({
          title: t("bot_details.modals.auth.title"),
          message: t("bot_details.modals.auth.vote_message"),
        });
        setShowSuccessModal(true);
        return;
      }

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(`${BACKEND_URL}/api/bots/vote/cooldown`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.canVote) {
        // Pode votar, abrir modal
        setShowVoteModal(true);
      } else if (data.cooldown) {
        // Em cooldown, mostrar modal com contador em tempo real
        setCooldownSeconds(data.remainingSeconds);
        setShowCooldownModal(true);
      }
    } catch (err) {
      console.error("Erro ao verificar cooldown:", err);
    }
  };

  // useEffect para atualizar o contador de cooldown em tempo real
  useEffect(() => {
    let interval;
    if (showCooldownModal && cooldownSeconds > 0) {
      interval = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setShowCooldownModal(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showCooldownModal, cooldownSeconds]);

  // Funções para DaaS (Dashboard)
  const handleDashboardClick = async () => {
    if (!authService.isAuthenticated()) {
      setModalConfig({
        title: t("bot_details.modals.dashboard_auth.title"),
        message: t("bot_details.modals.dashboard_auth.message"),
      });
      setShowSuccessModal(true);
      return;
    }

    setLoadingGuilds(true);
    setShowServerModal(true);
    try {
      const guilds = await authService.fetchGuilds();
      setUserGuilds(guilds || []);
    } catch (error) {
      console.error("Erro ao carregar servidores:", error);
    } finally {
      setLoadingGuilds(false);
    }
  };

  const handleSelectServer = async (guildId) => {
    setVerifyingPresence(true);
    try {
      const response = await authService.backendFetch(`/api/dashboard/check-presence/${botId}/${guildId}`);

      if (!response || !response.ok) {
        const data = response ? await response.json() : { error: t("bot_details.modals.presence.error") };
        setModalConfig({
          title: t("bot_details.modals.error.ops"),
          message: data.error || t("bot_details.modals.presence.error"),
        });
        setShowSuccessModal(true);
        return;
      }

      const data = await response.json();

      if (data.success) {
        if (data.isInGuild) {
          // Bot já está no servidor, ir direto para o dashboard
          navigate(`/dashboard/${botId}/${guildId}`);
        } else {
          // Bot NÃO está no servidor, levar para invite
          setModalConfig({
            title: t("bot_details.modals.presence.title") || t("bot_details.modals.error.title"),
            message: t("bot_details.modals.presence.not_found", { botName: data.botName }),
          });
          setShowSuccessModal(true);
          window.open(data.inviteUrl, "_blank");
        }
      } else {
        setModalConfig({
          title: t("bot_details.modals.error.ops"),
          message: data.error || t("bot_details.modals.presence.error"),
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Erro ao selecionar servidor:", error);
    } finally {
      setVerifyingPresence(false);
    }
  };

  // Função para formatar tempo de cooldown
  const formatCooldownTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  // Callback para quando o voto for bem-sucedido
  const handleVoteSuccess = () => {
    // Fechar modal de voto
    setShowVoteModal(false);

    // Mostrar modal de sucesso
    setModalConfig({
      title: t("bot_details.modals.vote.title"),
      message: t("bot_details.modals.vote.message"),
    });
    setShowSuccessModal(true);

    // Aguardar 5 segundos e recarregar
    setTimeout(() => {
      window.location.reload();
    }, 5000);
  };

  // Função para enviar denúncia
  const handleSubmitReport = async () => {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        setModalConfig({
          title: t("bot_details.modals.auth.title"),
          message: t("bot_details.modals.auth.report_message"),
        });
        setShowSuccessModal(true);
        setShowReportModal(false);
        return;
      }

      if (!reportReason || !reportDescription) {
        setShowReportModal(false);
        setShouldReopenReportModal(true);
        setModalConfig({
          title: t("bot_details.modals.validation.title"),
          message: t("bot_details.modals.validation.fill_all"),
        });
        setShowSuccessModal(true);
        return;
      }

      if (reportDescription.length < 10) {
        setShowReportModal(false);
        setShouldReopenReportModal(true);
        setModalConfig({
          title: t("bot_details.modals.validation.title"),
          message: t("bot_details.modals.validation.desc_min"),
        });
        setShowSuccessModal(true);
        return;
      }

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(`${BACKEND_URL}/api/reports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          targetType: "bot",
          targetId: botId,
          reason: reportReason,
          description: reportDescription,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setModalConfig({
          title: t("bot_details.modals.report.success_title"),
          message: t("bot_details.modals.report.success_message"),
        });
        setShowSuccessModal(true);
        setShowReportModal(false);
        setReportReason("");
        setReportDescription("");
      } else {
        setShowReportModal(false);
        setModalConfig({
          title: t("bot_details.modals.error.ops"),
          message: data.error || t("bot_details.modals.report.error_submit"),
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Erro ao enviar denúncia:", error);
      setShowReportModal(false);
      setModalConfig({
        title: t("bot_details.modals.error.title"),
        message: t("bot_details.modals.report.error_generic"),
      });
      setShowSuccessModal(true);
    }
  };

  // Função para excluir uma atualização
  const confirmDeleteUpdate = async () => {
    if (!updateToDeleteId) return;

    try {
      const token = authService.getAccessToken();
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(`${BACKEND_URL}/api/bots/${botId}/updates/${updateToDeleteId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setModalConfig({
          title: t("bot_details.reviews.success.title"),
          message: t("bot_details.updates.modal.delete_success"),
        });
        setShowSuccessModal(true);
        fetchBotUpdates();
      } else {
        const data = await response.json();
        setModalConfig({
          title: t("bot_details.modals.error.title"),
          message: data.error || t("bot_details.updates.modal.delete_error"),
        });
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error("Erro ao excluir update:", err);
    } finally {
      setUpdateToDeleteId(null);
    }
  };

  const handleDeleteUpdate = (updateId) => {
    setUpdateToDeleteId(updateId);
    setShowDeleteConfirmModal(true);
  };

  // Função para fechar o modal de sucesso e reabrir o modal de denúncia se necessário
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    if (shouldReopenReportModal) {
      setShouldReopenReportModal(false);
      setShowReportModal(true);
    }
  };

  // Função para alternar "útil" em uma avaliação
  const handleToggleHelpful = async (reviewId) => {
    try {
      const token = authService.getAccessToken();
      if (!token) {
        setModalConfig({
          title: t("bot_details.modals.auth.title"),
          message: t("bot_details.modals.auth.helpful_message"),
        });
        setShowSuccessModal(true);
        return;
      }

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(`${BACKEND_URL}/api/bots/${botId}/reviews/${reviewId}/helpful`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        // Atualizar o estado local do bot para refletir a mudança imediatamente
        setBot((prevBot) => ({
          ...prevBot,
          reviews: prevBot.reviews.map((rev) => {
            if (rev.id === reviewId) {
              const isAdding = data.action === "added";
              return {
                ...rev,
                helpfulCount: isAdding ? (rev.helpfulCount || 0) + 1 : Math.max(0, (rev.helpfulCount || 0) - 1),
                userFoundHelpful: isAdding,
              };
            }
            return rev;
          }),
        }));
      } else {
        // Se o erro for que é a própria review, mostrar mensagem
        if (response.status === 400) {
          setModalConfig({
            title: t("bot_details.modals.error.ops"),
            message: data.error,
          });
          setShowSuccessModal(true);
        }
      }
    } catch (err) {
      console.error("Erro ao alternar helpful:", err);
    }
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "pt" ? "pt-BR" : "en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="bot-details-page">
        <div className="container" style={{ padding: "4rem 0", textAlign: "center" }}>
          <div className="loading-spinner">{t("editBot.loading")}</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !bot) {
    return (
      <div className="bot-details-page">
        <div className="container" style={{ padding: "4rem 0", textAlign: "center" }}>
          <h2>❌ {error}</h2>
          <Link to="/bots" className="btn btn-primary" style={{ marginTop: "2rem" }}>
            {t("bot_details.hero.back_to_bots")}
          </Link>
        </div>
      </div>
    );
  }

  // Paginação de comandos
  const commandsPerPage = 5;
  const totalCommandsPages = Math.ceil((bot?.commands?.length || 0) / commandsPerPage);
  const startIndex = (commandsPage - 1) * commandsPerPage;
  const endIndex = startIndex + commandsPerPage;
  const currentCommands = (bot?.commands || []).slice(startIndex, endIndex);

  // Identificar usuário logado e verificar se já avaliou
  const currentUser = authService.getUser();
  const hasAlreadyReviewed = bot?.reviews?.some((r) => String(r.userId) === String(currentUser?.id));
  const allReviews = bot?.reviews || [];

  // Paginação de reviews
  const reviewsPerPage = 5;
  const totalReviewsPages = Math.ceil(allReviews.length / reviewsPerPage);
  const reviewStartIndex = (reviewsPage - 1) * reviewsPerPage;
  const reviewEndIndex = reviewStartIndex + reviewsPerPage;
  const currentReviews = allReviews.slice(reviewStartIndex, reviewEndIndex);

  const isWhiteLabelEligible =
    bot?.apiTier === "business" || (bot?.apiTier === "premium" && bot?.owner?.badges?.includes("Apoiador Rincko")) || bot?.owner?.badges?.includes("Apoiador Lan Code");

  return (
    <div className="bot-details-page">
      {isWhiteLabelEligible ? (
        <BusinessBotView
          bot={bot}
          onVote={handleVoteClick}
          onDashboard={handleDashboardClick}
          palette={typeof bot.whiteLabelSettings === "string" ? JSON.parse(bot.whiteLabelSettings).palette : bot.whiteLabelSettings?.palette || "midnight"}
        />
      ) : (
        <>
          {/* Hero Section */}
          <div className="bot-hero">
            <div className="bot-hero-background"></div>
            <div className="container">
              <Link to="/bots" className="back-button">
                <span>←</span> {t("bot_details.hero.back_to_bots")}
              </Link>

              <div className="bot-hero-content">
                <div className="bot-hero-left">
                  <div className="bot-avatar-hero">
                    {bot.avatar?.startsWith("http") ? (
                      <img
                        src={bot.avatar}
                        alt={bot.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                        }}
                      />
                    ) : (
                      bot.avatar
                    )}
                    {bot.verified && <div className="verified-badge-hero">✓</div>}
                  </div>
                  <div className="bot-hero-info">
                    <h1 className="bot-hero-title">
                      {bot.name}
                      {bot.apiTier === "premium" && <span className="tier-badge-inline premium">⭐ {t("bot_details.hero.premium")}</span>}
                      {bot.apiTier === "business" && <span className="tier-badge-inline business">🚀 {t("bot_details.hero.business")}</span>}
                    </h1>
                    <p className="bot-hero-tagline">{bot.tagline}</p>
                    <div className="bot-hero-meta">
                      <span className="bot-rating">⭐ {bot.rating}</span>
                      <span className="bot-servers">
                        👥 {bot.servers >= 1000 ? `${(bot.servers / 1000).toFixed(1)}K` : bot.servers}{" "}
                        {bot.servers === 1 ? t("bot_details.hero.servers_singular") : t("bot_details.hero.servers_plural")}
                      </span>
                      <span className="bot-users">
                        👤 {bot.users >= 1000 ? `${(bot.users / 1000).toFixed(1)}K` : bot.users}{" "}
                        {bot.users === 1 ? t("bot_details.hero.users_singular") : t("bot_details.hero.users_plural")}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bot-hero-actions">
                  {bot.status === "reanalysis" ? (
                    <div
                      style={{
                        background: "linear-gradient(135deg, #2a2d3a 0%, #1f2229 100%)",
                        border: "2px solid #FF9900",
                        padding: "2rem",
                        borderRadius: "16px",
                        textAlign: "center",
                        boxShadow: "0 8px 24px rgba(255, 153, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "3.5rem",
                          marginBottom: "1rem",
                          animation: "spin 3s linear infinite",
                        }}
                      >
                        🔄
                      </div>
                      <h3
                        style={{
                          color: "#FF9900",
                          margin: "0 0 0.5rem 0",
                          fontSize: "1.5rem",
                          fontWeight: "700",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("bot_details.hero.status.reanalysis.title")}
                      </h3>
                      <p
                        style={{
                          color: "rgba(255, 255, 255, 0.7)",
                          margin: 0,
                          fontSize: "0.95rem",
                          lineHeight: "1.6",
                        }}
                      >
                        {t("bot_details.hero.status.reanalysis.desc")}
                        <br />
                        <span style={{ color: "#FF9900", fontWeight: "500" }}>{t("bot_details.hero.status.reanalysis.soon")}</span>
                      </p>
                    </div>
                  ) : bot.status === "pending" ? (
                    <div
                      style={{
                        background: "linear-gradient(135deg, #2a2d3a 0%, #1f2229 100%)",
                        border: "2px solid #5865F2",
                        padding: "2rem",
                        borderRadius: "16px",
                        textAlign: "center",
                        boxShadow: "0 8px 24px rgba(88, 101, 242, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "3.5rem",
                          marginBottom: "1rem",
                          animation: "pulse 2s ease-in-out infinite",
                        }}
                      >
                        ⏳
                      </div>
                      <h3
                        style={{
                          color: "#5865F2",
                          margin: "0 0 0.5rem 0",
                          fontSize: "1.5rem",
                          fontWeight: "700",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("bot_details.hero.status.pending.title")}
                      </h3>
                      <p
                        style={{
                          color: "rgba(255, 255, 255, 0.7)",
                          margin: 0,
                          fontSize: "0.95rem",
                          lineHeight: "1.6",
                        }}
                      >
                        {t("bot_details.hero.status.pending.desc")}
                        <br />
                        <span style={{ color: "#5865F2", fontWeight: "500" }}>{t("bot_details.hero.status.pending.soon")}</span>
                      </p>
                    </div>
                  ) : bot.status === "banned" ? (
                    <div
                      style={{
                        background: "linear-gradient(135deg, #2a2d3a 0%, #1f2229 100%)",
                        border: "2px solid #DC143C",
                        padding: "2rem",
                        borderRadius: "16px",
                        textAlign: "center",
                        boxShadow: "0 8px 24px rgba(220, 20, 60, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
                      }}
                    >
                      <div style={{ fontSize: "3.5rem", marginBottom: "1rem" }}>🚫</div>
                      <h3
                        style={{
                          color: "#DC143C",
                          margin: "0 0 0.5rem 0",
                          fontSize: "1.5rem",
                          fontWeight: "700",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("bot_details.hero.status.banned.title")}
                      </h3>
                      <p
                        style={{
                          color: "rgba(255, 255, 255, 0.7)",
                          margin: 0,
                          fontSize: "0.95rem",
                          lineHeight: "1.6",
                        }}
                      >
                        {t("bot_details.hero.status.banned.desc")}
                        <br />
                        <span style={{ color: "#DC143C", fontWeight: "500" }}>{t("bot_details.hero.status.banned.warning")}</span>
                      </p>
                    </div>
                  ) : bot.status === "rejected" ? (
                    <div
                      style={{
                        background: "linear-gradient(135deg, #2a2d3a 0%, #1f2229 100%)",
                        border: "2px solid #9B59B6",
                        padding: "2rem",
                        borderRadius: "16px",
                        textAlign: "center",
                        boxShadow: "0 8px 24px rgba(155, 89, 182, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
                      }}
                    >
                      <div style={{ fontSize: "3.5rem", marginBottom: "1rem" }}>❌</div>
                      <h3
                        style={{
                          color: "#9B59B6",
                          margin: "0 0 0.5rem 0",
                          fontSize: "1.5rem",
                          fontWeight: "700",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {t("bot_details.hero.status.rejected.title")}
                      </h3>
                      <p
                        style={{
                          color: "rgba(255, 255, 255, 0.7)",
                          margin: 0,
                          fontSize: "0.95rem",
                          lineHeight: "1.6",
                        }}
                      >
                        {t("bot_details.hero.status.rejected.desc")}
                        <br />
                        <span style={{ color: "#9B59B6", fontWeight: "500" }}>{t("bot_details.hero.status.rejected.action")}</span>
                      </p>
                    </div>
                  ) : (
                    <>
                      <a
                        href={bot.links.invite}
                        className="btn btn-primary btn-large justify-content-center add-server-button"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          trackEvent("bot_invite_click", {
                            bot_id: bot.id,
                            bot_name: bot.name,
                          })
                        }
                      >
                        <span>➕</span> {t("bot_details.hero.actions.add")}
                      </a>
                      <button
                        className="btn btn-vote btn-large justify-content-center"
                        onClick={() => {
                          trackEvent("click_vote_init", {
                            bot_id: bot.id,
                            bot_name: bot.name,
                          });
                          handleVoteClick();
                        }}
                      >
                        <span>🗳️</span>{" "}
                        {t("bot_details.hero.actions.vote", {
                          count: bot.votes,
                        })}
                      </button>

                      <button
                        className="btn btn-secondary btn-large dashboard-button justify-content-center"
                        style={{
                          borderColor: "var(--primary)",
                          color: "white",
                        }}
                        onClick={handleDashboardClick}
                      >
                        <span>🎮</span> {t("bot_details.hero.actions.dashboard")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="bot-tabs">
            <div className="container">
              <div className="tabs-nav">
                <button className={`tab-button ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>
                  📋 {t("bot_details.tabs.overview")}
                </button>
                <button className={`tab-button ${activeTab === "commands" ? "active" : ""}`} onClick={() => setActiveTab("commands")}>
                  ⌨️ {t("bot_details.tabs.commands")}
                </button>
                <button className={`tab-button ${activeTab === "updates" ? "active" : ""}`} onClick={() => setActiveTab("updates")}>
                  🚀 {t("bot_details.tabs.updates")}
                </button>
                {/* Aba de Avaliações - mostra apenas se o bot estiver aprovado */}
                {bot.status === "approved" && (
                  <button className={`tab-button ${activeTab === "reviews" ? "active" : ""}`} onClick={() => setActiveTab("reviews")}>
                    ⭐ {t("bot_details.tabs.reviews")}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bot-content">
            <div className="container">
              <div className="bot-content-wrapper">
                {/* Main Content */}
                <div className="bot-main-content">
                  {activeTab === "overview" && (
                    <div className="tab-content">
                      {/* Description */}
                      <section className="content-section">
                        <h2 className="section-title about-title" style={{ fontSize: "1.5rem" }}>
                          📖 {t("bot_details.overview.about")}
                        </h2>
                        <div className="bot-long-description markdown-body">
                          <ReactMarkdown>{bot.longDescription}</ReactMarkdown>
                        </div>
                      </section>

                      {/* Tags */}
                      {bot.tags && bot.tags.length > 0 && (
                        <section className="content-section">
                          <h2 className="section-title tags-title" style={{ fontSize: "2rem" }}>
                            🏷️ {t("bot_details.overview.tags")}
                          </h2>
                          <div className="bot-tags-list">
                            {bot.tags.map((tag) => (
                              <span key={tag} className="tag">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </section>
                      )}

                      {/* Categorias */}
                      {bot.categories && bot.categories.length > 0 && (
                        <section className="content-section">
                          <h2 className="section-title" style={{ fontSize: "2rem" }}>
                            🗂️ {t("bot_details.overview.categories")}
                          </h2>
                          <div className="bot-tags-list">
                            {bot.categories.map((cat) => (
                              <span key={cat} className="tag category-tag-badge">
                                {t(`addBot.step3.categories.${cat}`, cat)}
                              </span>
                            ))}
                          </div>
                        </section>
                      )}
                    </div>
                  )}

                  {activeTab === "commands" && (
                    <div className="tab-content">
                      <section className="content-section">
                        <h2 className="section-title" style={{ fontSize: "1.5rem", marginBottom: "10px" }}>
                          ⌨️ {t("bot_details.commands.title")}
                        </h2>
                        <p className="section-subtitle" style={{ fontSize: "1rem", marginBottom: "10px" }}>
                          {t("bot_details.commands.prefix")}: <code>{bot.prefix}</code>
                        </p>

                        <div className="commands-list">
                          {currentCommands.map((cmd, index) => (
                            <div key={index} className="command-item">
                              <div className="command-header">
                                <code className="command-name">{cmd.name}</code>
                                <span className="command-usage">{cmd.usage}</span>
                              </div>
                              <p className="command-description">{cmd.description}</p>
                            </div>
                          ))}
                        </div>

                        {/* Pagination */}
                        {totalCommandsPages > 1 && (
                          <div className="commands-pagination">
                            <button className="pagination-btn" onClick={() => setCommandsPage((prev) => Math.max(1, prev - 1))} disabled={commandsPage === 1}>
                              {t("bot_details.commands.pagination.prev")}
                            </button>

                            <div className="pagination-info">
                              {t("bot_details.commands.pagination.info", {
                                current: commandsPage,
                                total: totalCommandsPages,
                              })}
                            </div>

                            <button
                              className="pagination-btn"
                              onClick={() => setCommandsPage((prev) => Math.min(totalCommandsPages, prev + 1))}
                              disabled={commandsPage === totalCommandsPages}
                            >
                              {t("bot_details.commands.pagination.next")}
                            </button>
                          </div>
                        )}
                      </section>
                    </div>
                  )}

                  {activeTab === "updates" && (
                    <div className="tab-content">
                      <section className="content-section">
                        <div className="section-header-with-subtitle">
                          <h2 className="section-title" style={{ fontSize: "1.5rem", marginBottom: "10px" }}>
                            🚀 {t("bot_details.updates.title")}
                          </h2>
                          <p className="section-subtitle" style={{ fontSize: "1rem", marginBottom: "10px" }}>
                            {t("bot_details.updates.subtitle")}
                          </p>
                        </div>

                        <div className="updates-list">
                          {botUpdates && botUpdates.length > 0 ? (
                            botUpdates.slice((updatesPage - 1) * 3, updatesPage * 3).map((update) => (
                              <div key={update.id} className="update-card">
                                <div className="update-header">
                                  <div className="update-meta">
                                    <span className={`update-type ${update.type}`}>{t(`bot_details.updates.types.${update.type}`)}</span>
                                    <span className="update-version">
                                      {t("bot_details.updates.version")} {update.version}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                    }}
                                  >
                                    <span className="update-date">{new Date(update.published_at).toLocaleDateString()}</span>

                                    {/* Ações de Edição/Exclusão - Apenas para o dono */}
                                    {bot && authService.getUser()?.id === bot.owner?.id && (
                                      <div
                                        className="update-actions"
                                        style={{
                                          display: "flex",
                                          gap: "5px",
                                        }}
                                      >
                                        <button
                                          onClick={() => {
                                            setEditingUpdate(update);
                                            setShowUpdateModal(true);
                                          }}
                                          style={{
                                            background: "rgba(255, 255, 255, 0.1)",
                                            border: "none",
                                            color: "white",
                                            padding: "5px 8px",
                                            borderRadius: "5px",
                                            cursor: "pointer",
                                            fontSize: "0.8rem",
                                          }}
                                          title={t("bot_details.updates.actions.edit")}
                                        >
                                          ✏️
                                        </button>
                                        <button
                                          onClick={() => handleDeleteUpdate(update.id)}
                                          style={{
                                            background: "rgba(255, 68, 68, 0.1)",
                                            border: "none",
                                            color: "#ff4444",
                                            padding: "5px 8px",
                                            borderRadius: "5px",
                                            cursor: "pointer",
                                            fontSize: "0.8rem",
                                          }}
                                          title={t("bot_details.updates.actions.delete")}
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <h3 className="update-title">{update.title}</h3>
                                <div className="update-description markdown-body">
                                  <ReactMarkdown>{update.description}</ReactMarkdown>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="no-content-message">
                              <p>{t("bot_details.updates.no_updates")}</p>
                            </div>
                          )}
                        </div>

                        {/* Paginação de Updates */}
                        {botUpdates.length > 3 && (
                          <div className="commands-pagination" style={{ marginTop: "2rem" }}>
                            <button className="pagination-btn" onClick={() => setUpdatesPage((prev) => Math.max(1, prev - 1))} disabled={updatesPage === 1}>
                              {t("bot_details.commands.pagination.prev")}
                            </button>

                            <div className="pagination-info">
                              {t("bot_details.commands.pagination.info", {
                                current: updatesPage,
                                total: Math.ceil(botUpdates.length / 3),
                              })}
                            </div>

                            <button
                              className="pagination-btn"
                              onClick={() => setUpdatesPage((prev) => Math.min(Math.ceil(botUpdates.length / 3), prev + 1))}
                              disabled={updatesPage === Math.ceil(botUpdates.length / 3)}
                            >
                              {t("bot_details.commands.pagination.next")}
                            </button>
                          </div>
                        )}

                        {/* Botão de Postar Update - Visível apenas para o dono */}
                        {bot && authService.getUser()?.id === bot.owner?.id && (
                          <div className="post-update-container" style={{ marginTop: "2rem", textAlign: "center" }}>
                            <button
                              className="btn btn-primary"
                              onClick={() => setShowUpdateModal(true)}
                              style={{
                                background: "linear-gradient(135deg, #5865F2 0%, #4752C4 100%)",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "12px 24px",
                              }}
                            >
                              🚀 {t("bot_details.updates.post_button")}
                            </button>
                          </div>
                        )}
                      </section>
                    </div>
                  )}

                  {activeTab === "reviews" && (
                    <div className="tab-content">
                      {/* Write Review Card - Só aparece se não tiver avaliado */}
                      {!hasAlreadyReviewed && (
                        <section className="content-section write-review-section">
                          <h2 className="section-title" style={{ fontSize: "1.5rem", marginBottom: "10px" }}>
                            ✍️ {t("bot_details.reviews.title")}
                          </h2>
                          <p className="section-subtitle" style={{ fontSize: "1rem", marginBottom: "10px" }}>
                            {t("bot_details.reviews.subtitle")}
                          </p>

                          {/* Star Rating */}
                          <div className="star-rating-container">
                            <label className="star-rating-label">{t("bot_details.reviews.label")}:</label>
                            <div className="star-rating">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button key={star} className={`star-btn ${star <= userRating ? "active" : ""}`} onClick={() => setUserRating(star)} type="button">
                                  ⭐
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Comment Textarea */}
                          <div className="comment-container">
                            <label className="comment-label">{t("bot_details.reviews.comment_label")}:</label>
                            <textarea
                              className="comment-textarea"
                              placeholder={t("bot_details.reviews.placeholder")}
                              value={userComment}
                              onChange={(e) => setUserComment(e.target.value)}
                              rows={4}
                              maxLength={500}
                            />
                            <div className="comment-counter">
                              {t("bot_details.reviews.char_count", {
                                count: userComment.length,
                              })}
                            </div>
                          </div>

                          {/* Action Button */}
                          <div style={{ display: "flex", gap: "1rem" }}>
                            <button className="btn btn-primary publish-button" disabled={userRating === 0 || userComment.trim() === ""} onClick={handlePublishReview}>
                              {t("bot_details.reviews.publish")}
                            </button>
                          </div>
                        </section>
                      )}

                      {/* Reviews List */}
                      <section className="content-section">
                        <h2 className="section-title" style={{ fontSize: "1.5rem", marginBottom: "10px" }}>
                          ⭐ {t("bot_details.reviews.user_reviews")}
                        </h2>
                        <div className="reviews-list">
                          {/* Renderizar todas as reviews paginadas */}
                          {currentReviews.length > 0 ? (
                            currentReviews.map((review, index) => {
                              const isOwnReview = String(review.userId) === String(currentUser?.id);
                              return (
                                <div key={index} className={`review-item ${isOwnReview ? "user-own-review" : ""}`}>
                                  <div className="review-header">
                                    <div className="review-user-info">
                                      <div className={`review-avatar ${isOwnReview ? "user-review-avatar" : ""}`}>
                                        <img src={review.avatar} alt={review.user} />
                                      </div>
                                      <div className="review-user">
                                        <strong>
                                          {review.user}
                                          {isOwnReview && <span className="your-badge">{t("bot_details.reviews.your_review_badge")}</span>}
                                        </strong>
                                        <div className="review-rating">
                                          {"⭐".repeat(review.rating)}
                                          <span className="rating-number">({review.rating}/5)</span>
                                        </div>
                                      </div>
                                    </div>
                                    <span className="review-date">{formatDate(review.date)}</span>
                                  </div>
                                  {review.comment && <p className="review-comment">{review.comment}</p>}
                                  <div className="review-footer">
                                    <button
                                      className={`helpful-btn ${review.userFoundHelpful ? "active" : ""}`}
                                      onClick={() => handleToggleHelpful(review.id)}
                                      title={review.userFoundHelpful ? t("bot_details.reviews.remove_helpful") : t("bot_details.reviews.found_helpful")}
                                    >
                                      <span className="helpful-icon">👍</span>
                                      <span className="helpful-count">{review.helpfulCount > 0 ? review.helpfulCount : t("bot_details.reviews.helpful")}</span>
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div
                              className="no-reviews"
                              style={{
                                textAlign: "center",
                                padding: "2rem",
                                color: "var(--text-muted)",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "3rem",
                                  marginBottom: "1rem",
                                }}
                              >
                                💬
                              </div>
                              <p>{t("bot_details.reviews.no_reviews")}</p>
                            </div>
                          )}

                          {/* Pagination para Reviews */}
                          {totalReviewsPages > 1 && (
                            <div className="commands-pagination reviews-pagination">
                              <button
                                className="pagination-btn"
                                onClick={() => {
                                  setReviewsPage((prev) => Math.max(1, prev - 1));
                                  window.scrollTo({
                                    top: document.querySelector(".reviews-list")?.offsetTop - 100,
                                    behavior: "smooth",
                                  });
                                }}
                                disabled={reviewsPage === 1}
                              >
                                {t("bot_details.commands.pagination.prev")}
                              </button>

                              <div className="pagination-info">
                                {t("bot_details.commands.pagination.info", {
                                  current: reviewsPage,
                                  total: totalReviewsPages,
                                })}
                              </div>

                              <button
                                className="pagination-btn"
                                onClick={() => {
                                  setReviewsPage((prev) => Math.min(totalReviewsPages, prev + 1));
                                  window.scrollTo({
                                    top: document.querySelector(".reviews-list")?.offsetTop - 100,
                                    behavior: "smooth",
                                  });
                                }}
                                disabled={reviewsPage === totalReviewsPages}
                              >
                                {t("bot_details.commands.pagination.next")}
                              </button>
                            </div>
                          )}
                        </div>
                      </section>
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <aside className="bot-sidebar">
                  {/* Stats */}
                  <div className="sidebar-card">
                    <h3 className="sidebar-title">📋 {t("bot_details.sidebar.info_title")}</h3>
                    <div className="stats-list">
                      <div className="stat-item">
                        <span className="stat-label">{t("bot_details.sidebar.prefix")}</span>
                        <span className="stat-value">
                          <code>{bot.prefix}</code>
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">{t("bot_details.sidebar.servers")}</span>
                        <span className="stat-value">{bot.servers >= 1000 ? `${(bot.servers / 1000).toFixed(1)}K` : bot.servers}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">{t("bot_details.sidebar.users")}</span>
                        <span className="stat-value">{bot.users >= 1000 ? `${(bot.users / 1000).toFixed(1)}K` : bot.users}</span>
                      </div>
                      <div className="stat-item languages-stat">
                        <span className="stat-label">{t("bot_details.sidebar.languages")}</span>
                        <div className="languages-list-sidebar">
                          {bot.languages && bot.languages.length > 0 ? (
                            bot.languages.map((lang) => (
                              <span key={lang.code} title={lang.name} className="lang-flag-sidebar">
                                {lang.flag}
                              </span>
                            ))
                          ) : (
                            <span className="stat-value">{t("bot_details.sidebar.not_applicable")}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Owner */}
                  <div className="sidebar-card">
                    <h3 className="sidebar-title">👨‍💻 {t("bot_details.sidebar.dev_title")}</h3>
                    <Link to={`/profile/${bot.owner.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                      <div className="owner-info">
                        <div className="owner-avatar">
                          {bot.owner.avatar?.startsWith("http") ? (
                            <img
                              src={bot.owner.avatar}
                              alt={bot.owner.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                              }}
                            />
                          ) : (
                            bot.owner.avatar
                          )}
                        </div>
                        <div>
                          <div className="owner-name">
                            {bot.owner.name}
                            {bot.owner.verified && <span className="verified-icon">✓</span>}
                          </div>
                          <div className="owner-role">{t("bot_details.sidebar.verified_dev")}</div>
                        </div>
                      </div>
                    </Link>
                  </div>

                  {/* Social Links */}
                  {(bot.links.website || bot.links.support || bot.links.github || bot.links.twitter || bot.links.instagram || bot.links.youtube) && (
                    <div className="sidebar-card">
                      <h3 className="sidebar-title">🌐 {t("bot_details.sidebar.social_title")}</h3>
                      <div className="v2-social-links" style={{ justifyContent: "flex-start", marginTop: "10px" }}>
                        {bot.links.website && (
                          <a href={bot.links.website} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.website")}>
                            🌐
                          </a>
                        )}
                        {bot.links.support && (
                          <a href={bot.links.support} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.support")}>
                            💬
                          </a>
                        )}
                        {bot.links.github && (
                          <a href={bot.links.github} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.github")}>
                            🐙
                          </a>
                        )}
                        {bot.links.twitter && (
                          <a href={bot.links.twitter} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.twitter")}>
                            🐦
                          </a>
                        )}
                        {bot.links.instagram && (
                          <a href={bot.links.instagram} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.instagram")}>
                            📸
                          </a>
                        )}
                        {bot.links.youtube && (
                          <a href={bot.links.youtube} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.youtube")}>
                            📺
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Support */}
                  {bot.status === "approved" && (
                    <div className="sidebar-card">
                      <h3 className="sidebar-title">💬 {t("bot_details.sidebar.support_title")}</h3>
                      <p className="sidebar-text">{t("bot_details.sidebar.support_desc")}</p>
                      <a href={bot.links.support} className="btn btn-outline btn-block btn-support-server" target="_blank" rel="noopener noreferrer">
                        {t("bot_details.sidebar.support_btn")}
                      </a>
                      {/* Botão de denúncia só aparece se o bot não for do próprio usuário */}
                      {currentUser?.id !== bot.owner.id && (
                        <button className="btn btn-danger btn-block btn-denunciar-bot" style={{ marginTop: "0.5rem" }} onClick={() => setShowReportModal(true)}>
                          🚨 {t("bot_details.sidebar.report_btn")}
                        </button>
                      )}
                    </div>
                  )}
                </aside>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Interactivity Modals (Universal) */}
      <VoteModal isOpen={showVoteModal} onClose={() => setShowVoteModal(false)} bot={bot} onVoteSuccess={handleVoteSuccess} />

      <SuccessModal
        isOpen={showCooldownModal}
        onClose={() => setShowCooldownModal(false)}
        title={t("bot_details.modals.cooldown.title")}
        message={t("bot_details.modals.cooldown.message", {
          time: formatCooldownTime(cooldownSeconds),
        })}
      />

      <SuccessModal isOpen={showSuccessModal} onClose={handleCloseSuccessModal} title={modalConfig.title} message={modalConfig.message} />

      {showReportModal && (
        <div className="modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🚨 {t("bot_details.modals.report.title")}</h2>
              <button className="modal-close" onClick={() => setShowReportModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="reportReason">{t("bot_details.modals.report.reason_label")}</label>
                <select id="reportReason" className="form-control" value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                  <option value="">{t("bot_details.modals.report.reason_select")}</option>
                  <option value="Spam">{t("bot_details.modals.report.reasons.spam")}</option>
                  <option value="Conteúdo Inapropriado">{t("bot_details.modals.report.reasons.inappropriate")}</option>
                  <option value="Comportamento Abusivo">{t("bot_details.modals.report.reasons.abuse")}</option>
                  <option value="Bot Offline">{t("bot_details.modals.report.reasons.offline")}</option>
                  <option value="Informações Falsas">{t("bot_details.modals.report.reasons.fake")}</option>
                  <option value="Violação de Termos">{t("bot_details.modals.report.reasons.terms")}</option>
                  <option value="Outro">{t("bot_details.modals.report.reasons.other")}</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="reportDescription">{t("bot_details.modals.report.desc_label")}</label>
                <textarea
                  id="reportDescription"
                  className="form-control"
                  rows="5"
                  placeholder={t("bot_details.modals.report.desc_placeholder")}
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  maxLength={1000}
                />
                <small className="form-text">
                  {t("bot_details.modals.report.char_count", {
                    count: reportDescription.length,
                  })}
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowReportModal(false)}>
                {t("editBot.common.cancel")}
              </button>
              <button className="btn btn-danger btn-enviar-denuncia" onClick={handleSubmitReport}>
                {t("bot_details.modals.report.submit_btn")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Postar Update */}
      <UpdateBotModal
        isOpen={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setEditingUpdate(null);
        }}
        bot={bot}
        editingUpdate={editingUpdate}
        onRefresh={() => {
          fetchBotUpdates();
          setModalConfig({
            title: t("bot_details.reviews.success.title"),
            message: editingUpdate ? t("bot_details.updates.edit_success") : t("bot_details.updates.post_success"),
          });
          setShowSuccessModal(true);
        }}
      />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        onConfirm={confirmDeleteUpdate}
        title={t("bot_details.updates.modal.delete_confirm_title")}
        message={t("bot_details.updates.modal.delete_confirm")}
        confirmText={t("profile_page.bots.actions.delete")}
        cancelText={t("bot_details.updates.modal.cancel")}
        isDanger={true}
      />
      {/* Modal de Seleção de Servidor DaaS */}
      {showServerModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "500px", borderRadius: "16px" }}>
            <div className="modal-header">
              <h2>{t("bot_details.modals.select_server.title")}</h2>
              <button className="modal-close" onClick={() => setShowServerModal(false)} disabled={verifyingPresence}>
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: "450px", overflowY: "auto" }}>
              <Trans i18nKey="bot_details.modals.select_server.question" values={{ botName: bot.name }}>
                Em qual servidor você deseja configurar o <strong>{bot.name}</strong>?
              </Trans>

              {loadingGuilds ? (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <div className="loading-spinner"></div>
                  <p style={{ marginTop: "1rem" }}>{t("bot_details.modals.select_server.loading")}</p>
                </div>
              ) : userGuilds.length === 0 ? (
                <p
                  style={{
                    textAlign: "center",
                    padding: "1rem",
                    color: "#ff6b6b",
                  }}
                >
                  {t("bot_details.modals.select_server.no_servers")}
                </p>
              ) : (
                <div className="server-list" style={{ display: "grid", gap: "0.75rem" }}>
                  {userGuilds.map((guild) => (
                    <button
                      key={guild.id}
                      className="server-item-btn"
                      onClick={() => handleSelectServer(guild.id)}
                      disabled={verifyingPresence}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        padding: "1rem",
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "10px",
                        color: "white",
                        textAlign: "left",
                        transition: "all 0.2s",
                        cursor: verifyingPresence ? "wait" : "pointer",
                      }}
                    >
                      {guild.icon ? (
                        <img
                          src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`}
                          alt=""
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            background: "var(--primary)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.2rem",
                          }}
                        >
                          {guild.name.charAt(0)}
                        </div>
                      )}
                      <span style={{ fontWeight: "600", flex: 1 }}>{guild.name}</span>
                      {verifyingPresence && <div className="loading-spinner-small"></div>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowServerModal(false)} disabled={verifyingPresence}>
                {t("bot_details.modals.select_server.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BotDetailsPage;
